import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { Button, Space, Table } from '@bedrock/components';
import { NoticeTextLeft, Plus } from '@bedrock/icons-react';
import { ProTablePlusForm } from '@mpc/common/src/components';
import { localPx2rem } from '#utils';
import { useControllableValue, useMemoizedFn } from 'ahooks';
import useAddEquityModal from '../useAddEquityModal';
import DiffTable from './DiffTable';
import NoData from '@bedrock/components/es/Empty/images/small/NoData';
import { $sType } from "@mpc/common";
import { DemandRightsContentVo } from '#services/api';

function hasDuplicate(arr, key) {
  const seen = new Set();
  
  if (!arr?.length) {
    return false;
  }

  for (const item of arr) {
    const v = item[key];

    if (v === null || v === undefined || v.trim() === '') {
      continue;
    }

    if (seen.has(v.trim())) {
      return true;
    }

    seen.add(v.trim());
  }
  return false;
}

export const getExpectRightsLabel = (key) => {
  if ($sType === 'outer') {
    return '额外权益'
  }
  if (key === 'supplier_expect_rights_list' || key === 'expect_rights_diff_list') {
    return '额外权益(供应商已确认）'
  }

  return '期望权益'
}

export const getFormItemKey = (detail, config = { inPlanning: false, view: false }) => {
  const { inPlanning, view } = config;
  if ($sType === 'outer' || inPlanning) {
    return 'expect_rights';
  }

  if ($sType === 'inner' && detail?.depth === 2) {
    return 'supplier_expect_rights_list';
  }
  if ($sType === 'inner' && detail?.depth === 1 && view) {
    return 'expect_rights_diff_list';
  }

  return 'expect_rights';
}

interface IProps {
  value?: any[];
  onChange?: (value: any[]) => void;
  showDiff?: boolean;
  differenceData?: any[]
  view?: boolean;
  detail?: any;
  formItemKey?: 'necessary_rights' | 'expect_rights_diff_list' | 'supplier_expect_rights_list' | 'expect_rights';
  inPlanning?: boolean;
  rightsList?: DemandRightsContentVo[];
  applyToTheWholeOrder?: boolean; // 是否应用到整单
  handleApplyToTheWholeOrder?: (value: any[]) => void; // 应用到整单的回调
  // 表格是否必填-必填是即使表格没有数据也展示表格
  tableRequired?: boolean;
}

const EquityFormTable: React.FC<IProps> = forwardRef((props, ref) => {
  const { detail, view, inPlanning, formItemKey, rightsList, applyToTheWholeOrder, handleApplyToTheWholeOrder = () => {}, tableRequired = true } = props;
  const tableRef = React.useRef<any>();
  const [value, setValue] = useControllableValue<any[]>(props, {
    defaultValue: []
  });

  const sortable = $sType === 'inner' && !view;
  const openAddEquityModal = useAddEquityModal({
    rowKey: 'rights_code',
    onOk: (data) => {
      setValue([
        ...(value || []),
        ...(data.map(v => ({
          ...v,
          actual_content: v.content,
        })) || [])
      ])
    },
  });

  useImperativeHandle(ref, () => ({
    validateFields: async () => {
      try {
        if (tableRef.current?.validate) {
          const values = await tableRef.current?.validate?.();
          if (values?.[0]?.message) {
            return Promise.reject(values?.[0]?.message)
          }
          if (hasDuplicate(value, 'right_code') || hasDuplicate(value, 'actual_content')) {
            return Promise.reject('权益存在重复')
          }
        }
        return Promise.resolve()
      } catch (error) {
        console.log(error, 'error');
        return Promise.reject()
      }
    }
  }))

  const columnsConfig = useMemoizedFn((key) => {
    if (key === 'content') {
      let hideInTable = true
      if (($sType === 'inner' && !view) || ($sType === 'inner' && inPlanning)) {
        hideInTable = false
      }

      if (detail?.depth === 2 && !inPlanning) {
        hideInTable = true
      }

      return {
        hideInTable
      }
    }

    return {}
  })

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      width: localPx2rem(48),
      hideInTable: sortable,
      render: (_, record, index) => `${index + 1}`,
    },
    {
      title: '权益库标准权益',
      dataIndex: 'content',
      width: localPx2rem(320),
      ...columnsConfig('content'),
    },
    {
      title: columnsConfig('content').hideInTable ? '权益内容' : '本单实际权益内容',
      dataIndex: 'actual_content',
      className: (view && columnsConfig('content').hideInTable) ? 'fw-bold' : undefined,
      editRender: ({ index, row, value }) => {
        if (view) {
          return {}
        }
        return {
          name: "input",
          editable: !view,
          rule: {
            required: !view,
            message: "此项 必填",
          },
          props: {
            maxLength: 200,
          }
        }
      }
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: localPx2rem(52),
      hideInTable: view,
      render: (_, record, index) => {
        return (
          <Button type='link' danger onClick={() => { setValue(value.filter((v, i) => index !== i)) }}>
            删除
          </Button>
        )
      },
    }
  ]

  const renderSummary = () => {
    const colSpan = columns.filter(v => !v.hideInTable).length + (sortable ? 1 : 0);
    return <Table.Summary>
      <Table.Summary.Row style={{ background: '#fff' }}>
        <Table.Summary.Cell index={0} colSpan={colSpan}>
          <Space size='small'>
            <Button
              size='small'
              type='checked'
              icon={<Plus />}
              onClick={() => {
                if ($sType === 'inner') {
                  openAddEquityModal({
                    disabledRows: value || [],
                    rightsList,
                  })
                } else {
                  setValue([...(value || []), {}])
                }
              }}
            >
              新增权益
            </Button>
            {applyToTheWholeOrder && <Button
              icon={<NoticeTextLeft />}
              type="checked-neutral"
              onClick={() => handleApplyToTheWholeOrder(value)}
              size='small'
            >
              应用至整单
            </Button>}
          </Space>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  }

  const renderBottom = () => {
    return view ? null : (
      <Space size='small' className='m-t-8'>
        <Button
          size='small'
          type='checked'
          icon={<Plus />}
          onClick={() => {
            if ($sType === 'inner') {
              openAddEquityModal({
                disabledRows: value || [],
                rightsList,
              })
            } else {
              setValue([...(value || []), {}])
            }
          }}
        >
          新增权益
        </Button>
        {applyToTheWholeOrder && <Button
          icon={<NoticeTextLeft />}
          type="checked-neutral"
          onClick={() => handleApplyToTheWholeOrder(value)}
          size='small'
        >
          应用至整单
        </Button>}
      </Space>
    )
  }

  if (view && !value?.length) {
    return <>--</>
  }

  if (formItemKey === 'expect_rights_diff_list') {
    return <div style={{ width: '100%' }}>
      <DiffTable value={value} />
    </div>
  }

  return (
    <div style={{ width: '100%' }}>
      {
        detail?.state === 'pending_quote' && !inPlanning && $sType === 'outer' && (
          <div style={{color: '#E08216', marginBottom: localPx2rem(4)}}>
            {formItemKey === 'necessary_rights' ? 
            '必要权益：当前合作内容需要配套的权益或要求':
            '额外权益：当前合作内容期望的权益或加分项，供应商可确认保留或增/改/删'}
          </div>
        ) 
      }
      {/* 非必填 且 无数据时 表格不显示 */}
      {!tableRequired && !value?.length ? null :
      <ProTablePlusForm
        ref={tableRef}
        style={{ width: '100%' }}
        scroll={{ 'x': 'max-content' }}
        sortable={sortable}
        value={value}
        onChange={setValue}
        columns={columns}
        // summary={!view ? renderSummary : undefined}
      />}
      {renderBottom()}
    </div>
  )
})

export default EquityFormTable;
